// src/Studio/patterns/PatternEngine.ts
// SVG pattern generator — 100 patterns across 10 categories

export interface PatternConfig {
  color: string;     // Main pattern stroke/fill color
  opacity: number;   // 0–1 overall opacity
  thickness: number; // Stroke width in px (0.5–8)
  spacing: number;   // Tile/cell size in px (8–80)
  rotation: number;  // Pattern rotation in degrees (0–360)
  blendMode: string; // CSS blend mode
}

export const DEFAULT_PATTERN_CONFIG: PatternConfig = {
  color: '#ffffff',
  opacity: 0.5,
  thickness: 1,
  spacing: 24,
  rotation: 0,
  blendMode: 'normal',
};

export interface PatternDef {
  id: string;
  name: string;
  category: string;
  defaults?: Partial<PatternConfig>;
  tile: (cfg: PatternConfig) => { tw: number; th: number; svg: string };
}

export interface PatternCategory {
  id: string;
  name: string;
}

export const PATTERN_CATEGORIES: PatternCategory[] = [
  { id: 'basic', name: 'Basic Geometry' },
  { id: 'dots', name: 'Dot Grids' },
  { id: 'triangles', name: 'Triangle Grids' },
  { id: 'hexagons', name: 'Hexagon Grids' },
  { id: 'tech', name: 'Tech / HUD' },
  { id: 'decorative', name: 'Decorative' },
  { id: 'wave', name: 'Wave / Organic' },
  { id: '3d', name: '3D-Style' },
  { id: 'creative', name: 'Creative' },
  { id: 'abstract', name: 'Abstract' },
  { id: 'animated', name: 'Animated ⚡' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────────
const f = (n: number) => parseFloat(n.toFixed(2)).toString();

function hexPts(cx: number, cy: number, r: number, flat = false): string {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i + (flat ? 0 : Math.PI / 6);
    return `${f(cx + r * Math.cos(a))},${f(cy + r * Math.sin(a))}`;
  }).join(' ');
}

function starPts(cx: number, cy: number, r1: number, r2: number, n: number): string {
  return Array.from({ length: n * 2 }, (_, i) => {
    const a = (Math.PI / n) * i - Math.PI / 2;
    const r = i % 2 === 0 ? r1 : r2;
    return `${f(cx + r * Math.cos(a))},${f(cy + r * Math.sin(a))}`;
  }).join(' ');
}

function wave(x0: number, y: number, w: number, amp: number, freq: number): string {
  let d = `M${f(x0)},${f(y)}`;
  for (let x = x0; x <= x0 + w; x += 1.5) {
    d += ` L${f(x)},${f(y + amp * Math.sin((x / w) * freq * Math.PI * 2))}`;
  }
  return d;
}

function rnd(i: number, j: number, s = 0): number {
  const h = (((i * 73856093) ^ (j * 19349663) ^ (s * 83492791)) * 2654435761) >>> 0;
  return (h % 10000) / 10000;
}

function line(x1: number, y1: number, x2: number, y2: number, c: string, t: number) {
  return `<line x1="${f(x1)}" y1="${f(y1)}" x2="${f(x2)}" y2="${f(y2)}" stroke="${c}" stroke-width="${t}"/>`;
}
function circle(cx: number, cy: number, r: number, c: string, fill = true, t = 0) {
  return `<circle cx="${f(cx)}" cy="${f(cy)}" r="${f(r)}" ${fill ? `fill="${c}"` : `fill="none" stroke="${c}" stroke-width="${t}"`}/>`;
}
function rect(x: number, y: number, w: number, h: number, c: string, fill = false, t = 1, rx = 0) {
  return `<rect x="${f(x)}" y="${f(y)}" width="${f(w)}" height="${f(h)}" rx="${f(rx)}" ${fill ? `fill="${c}"` : `fill="none" stroke="${c}" stroke-width="${f(t)}"`}/>`;
}

// ─── Pattern Registry ────────────────────────────────────────────────────────────
export const PATTERNS: PatternDef[] = [
  // ── BASIC GEOMETRY ──────────────────────────────────────────────────────────
  { id: 'sq-basic', name: 'Square grid', category: 'basic',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${s} 0L0 0 0 ${s}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'sq-dense', name: 'Dense square grid', category: 'basic', defaults: { spacing: 10 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${s} 0L0 0 0 ${s}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'sq-wide', name: 'Wide square grid', category: 'basic', defaults: { spacing: 48 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${s} 0L0 0 0 ${s}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'sq-thin', name: 'Thin square grid', category: 'basic', defaults: { thickness: 0.5 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${s} 0L0 0 0 ${s}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'sq-double', name: 'Double square grid', category: 'basic',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${s} 0L0 0 0 ${s}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           rect(s * 0.25, s * 0.25, s * 0.5, s * 0.5, c, false, t * 0.5) }) },

  { id: 'sq-offset', name: 'Offset square grid', category: 'basic',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s * 2, th: s,
      svg: `<path d="M0 0H${s * 2}M0 ${s}H${s * 2}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<path d="M0 0V${s}M${s} 0V${s}M${s * 2} 0V${s}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<rect x="${s / 2}" y="0" width="${s}" height="${s}" fill="none" stroke="${c}" stroke-width="${t * 0.5}" stroke-dasharray="2 2"/>` }) },

  { id: 'sq-perspective', name: 'Perspective square grid', category: 'basic',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<polygon points="${s * 0.1},0 ${s * 0.9},0 ${s},${s} 0,${s}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'sq-rounded', name: 'Rounded square grid', category: 'basic',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: rect(t / 2, t / 2, s - t, s - t, c, false, t, s * 0.2) }) },

  { id: 'sq-thick', name: 'Thick square grid', category: 'basic', defaults: { thickness: 3 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${s} 0L0 0 0 ${s}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'sq-broken', name: 'Broken square grid', category: 'basic',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${s} 0L0 0 0 ${s}" fill="none" stroke="${c}" stroke-width="${t}" stroke-dasharray="${s * 0.3} ${s * 0.15}"/>` }) },

  // ── DOT GRIDS ───────────────────────────────────────────────────────────────
  { id: 'dot-basic', name: 'Dot grid', category: 'dots',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: circle(s / 2, s / 2, t * 1.2, c) }) },

  { id: 'dot-micro', name: 'Micro dot grid', category: 'dots', defaults: { spacing: 8 },
    tile: ({ spacing: s, color: c }) => ({ tw: s, th: s,
      svg: circle(s / 2, s / 2, 0.8, c) }) },

  { id: 'dot-large', name: 'Large dot grid', category: 'dots', defaults: { spacing: 32 },
    tile: ({ spacing: s, color: c }) => ({ tw: s, th: s,
      svg: circle(s / 2, s / 2, s * 0.3, c) }) },

  { id: 'dot-offset', name: 'Offset dot grid', category: 'dots',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s * 2,
      svg: circle(s / 2, s / 2, t * 1.2, c) + circle(s, s * 1.5, t * 1.2, c) + circle(0, s * 1.5, t * 1.2, c) }) },

  { id: 'dot-hex', name: 'Hex dot grid', category: 'dots',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const hw = s; const hh = s * Math.sqrt(3);
      const r = t * 1.5;
      const pts = [
        [hw / 2, hh / 2], [0, 0], [hw, 0], [0, hh], [hw, hh],
        [0, hh / 2], [hw, hh / 2],
      ];
      return { tw: hw, th: hh, svg: pts.map(([x, y]) => circle(x, y, r, c)).join('') };
    } },

  { id: 'dot-dense', name: 'Dense dot grid', category: 'dots', defaults: { spacing: 10 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: circle(s / 2, s / 2, t * 1.2, c) }) },

  { id: 'dot-sparse', name: 'Sparse dot grid', category: 'dots', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: circle(s / 2, s / 2, t * 2, c) }) },

  { id: 'dot-hollow', name: 'Hollow dot grid', category: 'dots',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: circle(s / 2, s / 2, s * 0.25, c, false, t) }) },

  { id: 'dot-gradient', name: 'Gradient dot grid', category: 'dots', defaults: { spacing: 28 },
    tile: ({ spacing: s, color: c }) => ({ tw: s, th: s,
      svg: `<circle cx="${f(s/2)}" cy="${f(s/2)}" r="${f(s*0.3)}" fill="${c}" opacity="0.15"/>` +
           `<circle cx="${f(s/2)}" cy="${f(s/2)}" r="${f(s*0.18)}" fill="${c}" opacity="0.35"/>` +
           `<circle cx="${f(s/2)}" cy="${f(s/2)}" r="${f(s*0.08)}" fill="${c}" opacity="0.9"/>` }) },

  { id: 'dot-random', name: 'Random dot grid', category: 'dots', defaults: { spacing: 30 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({ length: 5 }, (_, i) =>
        circle(rnd(i, 0) * s, rnd(i, 1) * s, t * (0.8 + rnd(i, 2) * 0.8), c)
      ).join('') }) },

  // ── TRIANGLE GRIDS ──────────────────────────────────────────────────────────
  { id: 'tri-mesh', name: 'Triangle mesh grid', category: 'triangles',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const h = f(s * Math.sqrt(3) / 2);
      return { tw: s, th: Number(h),
        svg: `<polygon points="${f(s/2)},0 ${f(s)},${h} 0,${h}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'tri-dense', name: 'Dense triangle mesh', category: 'triangles', defaults: { spacing: 14 },
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const h = s * Math.sqrt(3) / 2;
      return { tw: s, th: h,
        svg: `<polygon points="${f(s/2)},0 ${f(s)},${f(h)} 0,${f(h)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'tri-thin', name: 'Thin triangle grid', category: 'triangles', defaults: { thickness: 0.5 },
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const h = s * Math.sqrt(3) / 2;
      return { tw: s, th: h,
        svg: `<polygon points="${f(s/2)},0 ${f(s)},${f(h)} 0,${f(h)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'tri-offset', name: 'Offset triangle grid', category: 'triangles',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const h = s * Math.sqrt(3) / 2;
      return { tw: s * 2, th: h * 2,
        svg: `<polygon points="${f(s/2)},0 ${f(s)},${f(h)} 0,${f(h)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             `<polygon points="${f(s*1.5)},0 ${f(s*2)},${f(h)} ${f(s)},${f(h)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             `<polygon points="${f(s)},${f(h)} ${f(s*1.5)},${f(h*2)} ${f(s/2)},${f(h*2)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'tri-iso', name: 'Isometric triangle grid', category: 'triangles',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const h = s * Math.sqrt(3) / 2;
      return { tw: s, th: h,
        svg: `<path d="M0,${f(h)} L${f(s/2)},0 L${f(s)},${f(h)} Z M${f(s/2)},0 L${f(s)},${f(h)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'tri-wave', name: 'Triangle wave grid', category: 'triangles',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s * 2, th: s,
      svg: `<polygon points="0,${f(s)} ${f(s)},0 ${f(s*2)},${f(s)}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'tri-star', name: 'Triangle star grid', category: 'triangles',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const h = s * Math.sqrt(3) / 2;
      const cx = s / 2; const cy = h / 2;
      return { tw: s, th: h,
        svg: `<polygon points="${f(cx)},${f(cy-h*0.4)} ${f(cx+s*0.4)},${f(cy+h*0.25)} ${f(cx-s*0.4)},${f(cy+h*0.25)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             `<polygon points="${f(cx)},${f(cy+h*0.4)} ${f(cx+s*0.4)},${f(cy-h*0.25)} ${f(cx-s*0.4)},${f(cy-h*0.25)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'tri-perspective', name: 'Triangle perspective grid', category: 'triangles',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s * 2, th: s,
      svg: `<polygon points="0,0 ${f(s*2)},0 ${f(s)},${f(s)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<polygon points="0,${f(s)} ${f(s*2)},${f(s)} ${f(s)},0" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'tri-network', name: 'Triangle network grid', category: 'triangles',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const h = s * Math.sqrt(3) / 2; const cx = s / 2; const cy = h / 2;
      return { tw: s, th: h,
        svg: `<polygon points="${f(cx)},0 ${f(s)},${f(h)} 0,${f(h)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             circle(cx, 0, t * 1.5, c) + circle(s, h, t * 1.5, c) + circle(0, h, t * 1.5, c) };
    } },

  { id: 'tri-broken', name: 'Broken triangle grid', category: 'triangles',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const h = s * Math.sqrt(3) / 2;
      return { tw: s, th: h,
        svg: `<polygon points="${f(s/2)},0 ${f(s)},${f(h)} 0,${f(h)}" fill="none" stroke="${c}" stroke-width="${t}" stroke-dasharray="${s*0.25} ${s*0.1}"/>` };
    } },

  // ── HEXAGON GRIDS ────────────────────────────────────────────────────────────
  { id: 'hex-basic', name: 'Hex grid', category: 'hexagons',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2; const tw = s * Math.sqrt(3); const th = s * 1.5;
      const cx = tw / 2; const cy = s;
      return { tw, th, svg: `<polygon points="${hexPts(cx, cy, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'hex-dense', name: 'Dense hex grid', category: 'hexagons', defaults: { spacing: 14 },
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2; const tw = s * Math.sqrt(3); const th = s * 1.5;
      return { tw, th, svg: `<polygon points="${hexPts(tw/2, s, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'hex-hollow', name: 'Hollow hex grid', category: 'hexagons', defaults: { thickness: 0.5 },
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2; const tw = s * Math.sqrt(3); const th = s * 1.5;
      return { tw, th, svg: `<polygon points="${hexPts(tw/2, s, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'hex-thick', name: 'Thick hex grid', category: 'hexagons', defaults: { thickness: 3 },
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2; const tw = s * Math.sqrt(3); const th = s * 1.5;
      return { tw, th, svg: `<polygon points="${hexPts(tw/2, s, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'hex-network', name: 'Hex network grid', category: 'hexagons',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2; const tw = s * Math.sqrt(3); const th = s * 1.5;
      const cx = tw / 2; const cy = s;
      return { tw, th,
        svg: `<polygon points="${hexPts(cx, cy, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             circle(cx, cy, t * 2, c) + Array.from({ length: 6 }, (_, i) => {
               const a = (Math.PI / 3) * i + Math.PI / 6;
               const x = cx + r * Math.cos(a); const y = cy + r * Math.sin(a);
               return line(cx, cy, x, y, c, t * 0.5);
             }).join('') };
    } },

  { id: 'hex-offset', name: 'Offset hex grid', category: 'hexagons',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2 * 0.9; const tw = s * Math.sqrt(3); const th = s * 3;
      return { tw, th,
        svg: `<polygon points="${hexPts(tw/2, s, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             `<polygon points="${hexPts(0, s*2.5, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             `<polygon points="${hexPts(tw, s*2.5, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'hex-star', name: 'Hex star grid', category: 'hexagons',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2; const tw = s * Math.sqrt(3); const th = s * 1.5;
      const cx = tw / 2; const cy = s;
      return { tw, th,
        svg: `<polygon points="${hexPts(cx, cy, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             `<polygon points="${starPts(cx, cy, r * 0.55, r * 0.25, 6)}" fill="none" stroke="${c}" stroke-width="${t * 0.7}"/>` };
    } },

  { id: 'hex-honeycomb', name: 'Hex honeycomb grid', category: 'hexagons',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2; const tw = s * Math.sqrt(3); const th = s * 1.5;
      return { tw, th,
        svg: `<polygon points="${hexPts(tw/2, s, r - t)}" fill="${c}" opacity="0.15"/>` +
             `<polygon points="${hexPts(tw/2, s, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'hex-perspective', name: 'Hex perspective grid', category: 'hexagons',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2; const tw = s * Math.sqrt(3); const th = s * 1.5; const cx = tw / 2; const cy = s;
      const pts = hexPts(cx, cy, r, true);
      return { tw, th,
        svg: `<polygon points="${pts}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             `<polygon points="${hexPts(cx, cy, r * 0.5, true)}" fill="none" stroke="${c}" stroke-width="${t * 0.5}"/>` };
    } },

  { id: 'hex-tech', name: 'Hex tech grid', category: 'hexagons',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s / 2; const tw = s * Math.sqrt(3); const th = s * 1.5; const cx = tw / 2; const cy = s;
      return { tw, th,
        svg: `<polygon points="${hexPts(cx, cy, r)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             circle(cx, cy, r * 0.2, c) +
             line(cx - r * 0.45, cy, cx - r * 0.12, cy, c, t) +
             line(cx + r * 0.12, cy, cx + r * 0.45, cy, c, t) };
    } },

  // ── TECH / HUD ───────────────────────────────────────────────────────────────
  { id: 'tech-hud', name: 'Sci-fi HUD grid', category: 'tech', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: circle(s/2, s/2, s*0.35, c, false, t) + circle(s/2, s/2, s*0.1, c) +
           line(0, s/2, s*0.12, s/2, c, t) + line(s*0.88, s/2, s, s/2, c, t) +
           line(s/2, 0, s/2, s*0.12, c, t) + line(s/2, s*0.88, s/2, s, c, t) +
           `<line x1="${f(s*0.25)}" y1="${f(s*0.25)}" x2="${f(s*0.32)}" y2="${f(s*0.32)}" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'tech-radar', name: 'Radar grid', category: 'tech', defaults: { spacing: 48 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: [0.15, 0.3, 0.45].map(r => circle(s/2, s/2, s*r, c, false, t * 0.5)).join('') +
           Array.from({length: 8}, (_, i) => {
             const a = (Math.PI / 4) * i;
             return line(s/2, s/2, s/2 + s*0.45*Math.cos(a), s/2 + s*0.45*Math.sin(a), c, t * 0.5);
           }).join('') }) },

  { id: 'tech-circuit', name: 'Circuit grid', category: 'tech', defaults: { spacing: 32 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: line(0, s/2, s*0.35, s/2, c, t) + line(s*0.35, s/2, s*0.35, s*0.15, c, t) +
           line(s*0.35, s*0.15, s*0.65, s*0.15, c, t) + line(s*0.65, s*0.15, s*0.65, s/2, c, t) +
           line(s*0.65, s/2, s, s/2, c, t) + circle(s*0.35, s/2, t*1.8, c) + circle(s*0.65, s/2, t*1.8, c) }) },

  { id: 'tech-matrix', name: 'Digital matrix grid', category: 'tech', defaults: { spacing: 12 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: rect(s*0.1, s*0.1, s*0.8, s*0.8, c, false, t, s*0.1) }) },

  { id: 'tech-target', name: 'Target grid', category: 'tech', defaults: { spacing: 48 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: [0.45, 0.32, 0.18].map((r, i) =>
        `<circle cx="${f(s/2)}" cy="${f(s/2)}" r="${f(s*r)}" fill="none" stroke="${c}" stroke-width="${t}" opacity="${0.4 + i * 0.3}"/>`
      ).join('') + line(s/2, s*0.01, s/2, s*0.99, c, t*0.4) + line(s*0.01, s/2, s*0.99, s/2, c, t*0.4) }) },

  { id: 'tech-measurement', name: 'Measurement grid', category: 'tech', defaults: { spacing: 20 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: line(0, 0, s, 0, c, t) + line(0, s/2, s, s/2, c, t*0.5) +
           Array.from({length: 5}, (_, i) => line(i*(s/4), 0, i*(s/4), i%2===0?s*0.3:s*0.15, c, t)).join('') }) },

  { id: 'tech-navigation', name: 'Navigation grid', category: 'tech', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: circle(s/2, s/2, s*0.4, c, false, t*0.5) + circle(s/2, s/2, t*1.5, c) +
           Array.from({length: 4}, (_, i) => {
             const a = i * Math.PI / 2 - Math.PI / 2;
             return `<polygon points="${f(s/2 + s*0.1*Math.cos(a))},${f(s/2 + s*0.1*Math.sin(a))} ${f(s/2 + s*0.2*Math.cos(a-0.25))},${f(s/2 + s*0.2*Math.sin(a-0.25))} ${f(s/2 + s*0.2*Math.cos(a+0.25))},${f(s/2 + s*0.2*Math.sin(a+0.25))}" fill="${c}"/>`;
           }).join('') }) },

  { id: 'tech-data', name: 'Data grid', category: 'tech', defaults: { spacing: 24 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: line(0, s, 0, 0, c, t) + line(0, s, s, s, c, t) +
           [0.3, 0.6, 0.45, 0.8, 0.5].map((h, i) =>
             `<rect x="${f(i*(s/5)+t)}" y="${f(s*(1-h))}" width="${f(s/5-t*2)}" height="${f(s*h)}" fill="${c}" opacity="0.5"/>`
           ).join('') }) },

  { id: 'tech-cyber', name: 'Cyber grid', category: 'tech', defaults: { spacing: 36 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M0,${f(s*0.3)} L${f(s*0.3)},0 H${f(s*0.7)} L${f(s)},${f(s*0.3)} V${f(s*0.7)} L${f(s*0.7)},${f(s)} H${f(s*0.3)} L0,${f(s*0.7)} Z" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'tech-scanner', name: 'Scanner grid', category: 'tech', defaults: { spacing: 16 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: line(0, s/2, s, s/2, c, t) +
           line(0, 0, s*0.2, s/2, c, t*0.4) + line(s*0.8, s/2, s, s, c, t*0.4) }) },

  // ── DECORATIVE ───────────────────────────────────────────────────────────────
  { id: 'deco-star', name: 'Star grid', category: 'decorative',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<polygon points="${starPts(s/2, s/2, s*0.35, s*0.15, 5)}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'deco-cross', name: 'Cross grid', category: 'decorative',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${f(s*0.35)},${f(s*0.1)} H${f(s*0.65)} V${f(s*0.35)} H${f(s*0.9)} V${f(s*0.65)} H${f(s*0.65)} V${f(s*0.9)} H${f(s*0.35)} V${f(s*0.65)} H${f(s*0.1)} V${f(s*0.35)} H${f(s*0.35)} Z" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'deco-diamond', name: 'Diamond grid', category: 'decorative',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<polygon points="${f(s/2)},${f(s*0.1)} ${f(s*0.9)},${f(s/2)} ${f(s/2)},${f(s*0.9)} ${f(s*0.1)},${f(s/2)}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'deco-snowflake', name: 'Snowflake grid', category: 'decorative', defaults: { spacing: 32 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 6}, (_, i) => {
        const a = (Math.PI / 3) * i; const r = s * 0.4;
        const x = f(s/2 + r * Math.cos(a)); const y = f(s/2 + r * Math.sin(a));
        return line(s/2, s/2, Number(x), Number(y), c, t) +
               line(Number(x), Number(y), Number(f(s/2 + r * 0.6 * Math.cos(a + 0.4))), Number(f(s/2 + r * 0.6 * Math.sin(a + 0.4))), c, t * 0.7) +
               line(Number(x), Number(y), Number(f(s/2 + r * 0.6 * Math.cos(a - 0.4))), Number(f(s/2 + r * 0.6 * Math.sin(a - 0.4))), c, t * 0.7);
      }).join('') + circle(s/2, s/2, t * 2, c) }) },

  { id: 'deco-flower', name: 'Flower grid', category: 'decorative', defaults: { spacing: 30 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 6}, (_, i) => {
        const a = (Math.PI / 3) * i; const r = s * 0.22;
        return circle(s/2 + r * Math.cos(a), s/2 + r * Math.sin(a), r, c, false, t);
      }).join('') + circle(s/2, s/2, s * 0.08, c) }) },

  { id: 'deco-pixel', name: 'Pixel grid', category: 'decorative', defaults: { spacing: 8 },
    tile: ({ spacing: s, color: c }) => ({ tw: s, th: s,
      svg: rect(s*0.15, s*0.15, s*0.7, s*0.7, c, true, 0, 0) }) },

  { id: 'deco-mosaic', name: 'Mosaic grid', category: 'decorative', defaults: { spacing: 28 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<polygon points="0,0 ${f(s*0.55)},0 ${f(s*0.55)},${f(s*0.45)} 0,${f(s*0.45)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<polygon points="${f(s*0.6)},0 ${f(s)},0 ${f(s)},${f(s*0.5)} ${f(s*0.6)},${f(s*0.5)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<polygon points="0,${f(s*0.5)} ${f(s*0.4)},${f(s*0.5)} ${f(s*0.4)},${f(s)} 0,${f(s)}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'deco-spark', name: 'Spark grid', category: 'decorative', defaults: { spacing: 28 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${f(s/2)},${f(s*0.1)} L${f(s*0.4)},${f(s*0.45)} L${f(s*0.1)},${f(s*0.45)} L${f(s*0.45)},${f(s*0.6)} L${f(s*0.3)},${f(s*0.9)} L${f(s/2)},${f(s*0.65)} L${f(s*0.7)},${f(s*0.9)} L${f(s*0.55)},${f(s*0.6)} L${f(s*0.9)},${f(s*0.45)} L${f(s*0.6)},${f(s*0.45)} Z" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'deco-firework', name: 'Firework grid', category: 'decorative', defaults: { spacing: 36 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 8}, (_, i) => {
        const a = (Math.PI / 4) * i;
        return line(s/2 + s*0.05*Math.cos(a), s/2 + s*0.05*Math.sin(a), s/2 + s*0.4*Math.cos(a), s/2 + s*0.4*Math.sin(a), c, t) +
               circle(s/2 + s*0.42*Math.cos(a), s/2 + s*0.42*Math.sin(a), t * 1.5, c);
      }).join('') + circle(s/2, s/2, t * 2.5, c) }) },

  { id: 'deco-circle-chain', name: 'Circle chain grid', category: 'decorative',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s * 2, th: s,
      svg: circle(s/2, s/2, s*0.35, c, false, t) + circle(s*1.5, s/2, s*0.35, c, false, t) }) },

  // ── WAVE / ORGANIC ───────────────────────────────────────────────────────────
  { id: 'wave-basic', name: 'Wave grid', category: 'wave',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s * 2, th: s,
      svg: `<path d="${wave(0, s/2, s*2, s*0.35, 1)}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'wave-flow', name: 'Flow grid', category: 'wave', defaults: { spacing: 20 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s * 2, th: s,
      svg: [0.25, 0.5, 0.75].map(oy =>
        `<path d="${wave(0, s*oy, s*2, s*0.12, 1)}" fill="none" stroke="${c}" stroke-width="${t}" opacity="${0.5 + oy * 0.5}"/>`
      ).join('') }) },

  { id: 'wave-curved', name: 'Curved grid', category: 'wave',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M0,${f(s)} Q${f(s/2)},0 ${f(s)},${f(s)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<path d="M0,0 Q${f(s/2)},${f(s)} ${f(s)},0" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'wave-spiral', name: 'Spiral grid', category: 'wave', defaults: { spacing: 36 },
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const cx = s/2; const cy = s/2;
      let d = `M${cx},${cy}`;
      for (let i = 0; i <= 360 * 1.5; i += 5) {
        const a = (i * Math.PI) / 180;
        const r = (i / 360) * s * 0.4;
        d += ` L${f(cx + r * Math.cos(a))},${f(cy + r * Math.sin(a))}`;
      }
      return { tw: s, th: s, svg: `<path d="${d}" fill="none" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: 'wave-ripple', name: 'Ripple grid', category: 'wave', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: [0.15, 0.3, 0.45].map((r, i) =>
        `<circle cx="${f(s/2)}" cy="${f(s/2)}" r="${f(s*r)}" fill="none" stroke="${c}" stroke-width="${t}" opacity="${0.3 + i * 0.35}"/>`
      ).join('') }) },

  { id: 'wave-bubble', name: 'Bubble grid', category: 'wave', defaults: { spacing: 32 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<ellipse cx="${f(s/2)}" cy="${f(s*0.55)}" rx="${f(s*0.32)}" ry="${f(s*0.38)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<path d="M${f(s*0.32)},${f(s*0.3)} Q${f(s*0.38)},${f(s*0.22)} ${f(s*0.46)},${f(s*0.28)}" fill="none" stroke="${c}" stroke-width="${t}" opacity="0.7"/>` }) },

  { id: 'wave-cloud', name: 'Cloud grid', category: 'wave', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${f(s*0.1)},${f(s*0.65)} Q${f(s*0.1)},${f(s*0.45)} ${f(s*0.25)},${f(s*0.45)} Q${f(s*0.25)},${f(s*0.3)} ${f(s*0.42)},${f(s*0.3)} Q${f(s*0.5)},${f(s*0.18)} ${f(s*0.62)},${f(s*0.28)} Q${f(s*0.78)},${f(s*0.22)} ${f(s*0.84)},${f(s*0.38)} Q${f(s)},${f(s*0.38)} ${f(s)},${f(s*0.58)} Z" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'wave-water', name: 'Water ripple grid', category: 'wave',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s * 2, th: s / 2,
      svg: `<path d="M0,${f(s/4)} C${f(s/2)},0 ${f(s)},${f(s/2)} ${f(s*1.5)},${f(s/4)} S${f(s*2)},0 ${f(s*2)},${f(s/4)}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'wave-torn', name: 'Torn grid', category: 'wave',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M0,0 L${f(s*0.2)},${f(s*0.1)} L${f(s*0.35)},0 L${f(s*0.5)},${f(s*0.12)} L${f(s*0.65)},${f(s*0.04)} L${f(s*0.8)},${f(s*0.15)} L${f(s)},${f(s*0.05)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<path d="M0,${f(s/2)} L${f(s*0.15)},${f(s*0.55)} L${f(s*0.35)},${f(s*0.48)} L${f(s*0.55)},${f(s*0.6)} L${f(s*0.75)},${f(s*0.5)} L${f(s)},${f(s*0.55)}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'wave-noise', name: 'Noise grid', category: 'wave', defaults: { spacing: 20 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 12}, (_, i) => {
        const x = rnd(i, 0) * s; const y = rnd(i, 1) * s;
        const a = rnd(i, 2) * Math.PI;
        const len = s * 0.1 + rnd(i, 3) * s * 0.15;
        return line(x, y, x + len * Math.cos(a), y + len * Math.sin(a), c, t * (0.5 + rnd(i, 4) * 0.5));
      }).join('') }) },

  // ── 3D-STYLE ─────────────────────────────────────────────────────────────────
  { id: '3d-floor', name: 'Perspective floor grid', category: '3d', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${f(s/2)},0 L0,${f(s)} M${f(s/2)},0 L${f(s)},${f(s)} M0,${f(s*0.4)} L${f(s)},${f(s*0.4)} M0,${f(s*0.7)} L${f(s)},${f(s*0.7)} M0,${f(s)} L${f(s)},${f(s)}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: '3d-cube', name: '3D cube grid', category: '3d', defaults: { spacing: 30 },
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const h = s / 2; const w = s * Math.sqrt(3) / 2;
      return { tw: w * 2, th: h * 3,
        svg: `<path d="M${f(w)},0 L${f(w*2)},${f(h)} L${f(w)},${f(h*2)} L0,${f(h)} Z" fill="none" stroke="${c}" stroke-width="${t}"/>` +
             `<path d="M0,${f(h)} L${f(w)},${f(h*2)} L${f(w)},${f(h*3)} L0,${f(h*2)} Z" fill="${c}" opacity="0.08" stroke="${c}" stroke-width="${t}"/>` +
             `<path d="M${f(w)},${f(h*2)} L${f(w*2)},${f(h)} L${f(w*2)},${f(h*2)} L${f(w)},${f(h*3)} Z" fill="${c}" opacity="0.15" stroke="${c}" stroke-width="${t}"/>` };
    } },

  { id: '3d-wireframe', name: '3D wireframe grid', category: '3d', defaults: { spacing: 28 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: rect(s*0.1, s*0.1, s*0.6, s*0.6, c, false, t) + rect(s*0.3, s*0.3, s*0.6, s*0.6, c, false, t) +
           line(s*0.1, s*0.1, s*0.3, s*0.3, c, t) + line(s*0.7, s*0.1, s*0.9, s*0.3, c, t) +
           line(s*0.1, s*0.7, s*0.3, s*0.9, c, t) + line(s*0.7, s*0.7, s*0.9, s*0.9, c, t) }) },

  { id: '3d-depth', name: 'Depth grid', category: '3d', defaults: { spacing: 32 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: rect(0, 0, s, s, c, false, t) + rect(s*0.15, s*0.15, s*0.7, s*0.7, c, false, t * 0.7) +
           rect(s*0.3, s*0.3, s*0.4, s*0.4, c, false, t * 0.4) }) },

  { id: '3d-tunnel', name: 'Tunnel grid', category: '3d', defaults: { spacing: 48 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: [0.48, 0.36, 0.24, 0.12].map((r, i) =>
        `<rect x="${f(s*(0.5-r))}" y="${f(s*(0.5-r))}" width="${f(s*r*2)}" height="${f(s*r*2)}" fill="none" stroke="${c}" stroke-width="${t}" opacity="${0.3+i*0.2}"/>`
      ).join('') }) },

  { id: '3d-horizon', name: 'Horizon grid', category: '3d', defaults: { spacing: 36 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 6}, (_, i) => {
        const y = s * (i / 5);
        const perspective = 0.5 + (i / 5) * 0.5;
        const x0 = s * (0.5 - 0.5 * perspective); const x1 = s * (0.5 + 0.5 * perspective);
        return `<line x1="${f(x0)}" y1="${f(y)}" x2="${f(x1)}" y2="${f(y)}" stroke="${c}" stroke-width="${f(t * (0.5 + (i/5)*0.8))}"/>`;
      }).join('') + Array.from({length: 5}, (_, i) => {
        const t0 = [s*0.5, s*0]; const t1 = [i*(s/4), s];
        return `<line x1="${f(t0[0])}" y1="${f(t0[1])}" x2="${f(t1[0])}" y2="${f(t1[1])}" stroke="${c}" stroke-width="${t*0.5}"/>`;
      }).join('') }) },

  { id: '3d-warp', name: 'Warp grid', category: '3d',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M0,${f(s/2)} Q${f(s/2)},0 ${f(s)},${f(s/2)} Q${f(s/2)},${f(s)} 0,${f(s/2)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<path d="M${f(s/2)},0 Q${f(s)},${f(s/2)} ${f(s/2)},${f(s)} Q0,${f(s/2)} ${f(s/2)},0" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: '3d-sphere', name: 'Sphere grid', category: '3d', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: circle(s/2, s/2, s*0.42, c, false, t) +
           `<path d="M${f(s*0.08)},${f(s/2)} Q${f(s/2)},${f(s*0.3)} ${f(s*0.92)},${f(s/2)}" fill="none" stroke="${c}" stroke-width="${t*0.6}"/>` +
           `<path d="M${f(s*0.08)},${f(s/2)} Q${f(s/2)},${f(s*0.7)} ${f(s*0.92)},${f(s/2)}" fill="none" stroke="${c}" stroke-width="${t*0.6}"/>` +
           `<path d="M${f(s/2)},${f(s*0.08)} Q${f(s*0.65)},${f(s/2)} ${f(s/2)},${f(s*0.92)}" fill="none" stroke="${c}" stroke-width="${t*0.6}"/>` }) },

  { id: '3d-globe', name: 'Globe grid', category: '3d', defaults: { spacing: 48 },
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const r = s * 0.43;
      return { tw: s, th: s,
        svg: circle(s/2, s/2, r, c, false, t) +
             Array.from({length: 4}, (_, i) => {
               const lat = -r + (r * 2 / 3) * (i + 0.5);
               const wr = Math.sqrt(Math.max(0, r * r - lat * lat));
               return `<ellipse cx="${f(s/2)}" cy="${f(s/2+lat)}" rx="${f(wr)}" ry="${f(wr*0.35)}" fill="none" stroke="${c}" stroke-width="${t*0.6}"/>`;
             }).join('') +
             `<ellipse cx="${f(s/2)}" cy="${f(s/2)}" rx="${f(r*0.4)}" ry="${f(r)}" fill="none" stroke="${c}" stroke-width="${t*0.6}"/>` +
             `<ellipse cx="${f(s/2)}" cy="${f(s/2)}" rx="${f(r)}" ry="${f(r*0.4)}" fill="none" stroke="${c}" stroke-width="${t*0.6}"/>` };
    } },

  { id: '3d-matrix', name: 'Matrix depth grid', category: '3d', defaults: { spacing: 14 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: rect(s*0.05, s*0.05, s*0.9, s*0.9, c, false, t, s*0.15) +
           circle(s/2, s/2, s * 0.15, c) }) },

  // ── CREATIVE ─────────────────────────────────────────────────────────────────
  { id: 'creative-lightning', name: 'Lightning grid', category: 'creative', defaults: { spacing: 32 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${f(s*0.55)},${f(s*0.05)} L${f(s*0.35)},${f(s*0.45)} L${f(s*0.55)},${f(s*0.45)} L${f(s*0.35)},${f(s*0.95)}" fill="none" stroke="${c}" stroke-width="${t}" stroke-linejoin="round"/>` }) },

  { id: 'creative-energy', name: 'Energy grid', category: 'creative', defaults: { spacing: 28 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 4}, (_, i) => {
        const a = (Math.PI / 2) * i; const len = s * (0.2 + rnd(i, 5) * 0.2);
        return `<path d="M${f(s/2)},${f(s/2)} L${f(s/2 + len*Math.cos(a))},${f(s/2 + len*Math.sin(a))} L${f(s/2 + len*0.7*Math.cos(a+0.4))},${f(s/2 + len*0.7*Math.sin(a+0.4))}" fill="none" stroke="${c}" stroke-width="${t}"/>`;
      }).join('') }) },

  { id: 'creative-particle', name: 'Particle grid', category: 'creative', defaults: { spacing: 24 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 8}, (_, i) =>
        circle(rnd(i,7)*s, rnd(i,8)*s, t*(0.6+rnd(i,9)*1.2), c)
      ).join('') }) },

  { id: 'creative-confetti', name: 'Confetti grid', category: 'creative', defaults: { spacing: 30 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: [0,1,2,3,4,5].map(i => {
        const x = rnd(i,10)*s; const y = rnd(i,11)*s; const a = rnd(i,12)*360;
        const shapes = [
          `<rect x="${f(x-t*1.5)}" y="${f(y-t*1.5)}" width="${f(t*3)}" height="${f(t*3)}" fill="${c}" transform="rotate(${a},${f(x)},${f(y)})"/>`,
          circle(x, y, t*2, c),
          `<polygon points="${f(x)},${f(y-t*2)} ${f(x+t*2)},${f(y+t*2)} ${f(x-t*2)},${f(y+t*2)}" fill="${c}"/>`,
        ];
        return shapes[i % 3];
      }).join('') }) },

  { id: 'creative-glow', name: 'Glow grid', category: 'creative', defaults: { spacing: 36 },
    tile: ({ spacing: s, color: c }) => ({ tw: s, th: s,
      svg: `<circle cx="${f(s/2)}" cy="${f(s/2)}" r="${f(s*0.38)}" fill="${c}" opacity="0.06"/>` +
           `<circle cx="${f(s/2)}" cy="${f(s/2)}" r="${f(s*0.22)}" fill="${c}" opacity="0.12"/>` +
           `<circle cx="${f(s/2)}" cy="${f(s/2)}" r="${f(s*0.1)}" fill="${c}" opacity="0.8"/>` }) },

  { id: 'creative-neon', name: 'Neon grid', category: 'creative', defaults: { spacing: 28, thickness: 1.5 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: rect(s*0.08, s*0.08, s*0.84, s*0.84, c, false, t, s*0.35) +
           `<filter id="nblur"><feGaussianBlur stdDeviation="1.2"/></filter>` +
           `<rect x="${f(s*0.08)}" y="${f(s*0.08)}" width="${f(s*0.84)}" height="${f(s*0.84)}" rx="${f(s*0.35)}" fill="none" stroke="${c}" stroke-width="${f(t*2.5)}" opacity="0.25" filter="url(#nblur)"/>` }) },

  { id: 'creative-pulse', name: 'Pulse grid', category: 'creative', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: [0.15, 0.28, 0.42].map((r, i) =>
        `<circle cx="${f(s/2)}" cy="${f(s/2)}" r="${f(s*r)}" fill="none" stroke="${c}" stroke-width="${f(t*(1.2-i*0.35))}" opacity="${1-i*0.3}"/>`
      ).join('') + circle(s/2, s/2, s*0.05, c) }) },

  { id: 'creative-ring', name: 'Ring grid', category: 'creative', defaults: { spacing: 28 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: circle(s/2, s/2, s*0.4, c, false, t) + circle(s/2, s/2, s*0.25, c, false, t * 0.6) + circle(s/2, s/2, t*1.5, c) }) },

  { id: 'creative-burst', name: 'Burst grid', category: 'creative', defaults: { spacing: 36 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 12}, (_, i) => {
        const a = (Math.PI / 6) * i;
        return line(s/2, s/2, s/2 + s*0.42*Math.cos(a), s/2 + s*0.42*Math.sin(a), c, i%2===0 ? t : t*0.5);
      }).join('') + circle(s/2, s/2, s*0.08, c, false, t) }) },

  { id: 'creative-orbit', name: 'Orbit grid', category: 'creative', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<ellipse cx="${f(s/2)}" cy="${f(s/2)}" rx="${f(s*0.38)}" ry="${f(s*0.18)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<ellipse cx="${f(s/2)}" cy="${f(s/2)}" rx="${f(s*0.18)}" ry="${f(s*0.38)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           circle(s/2, s/2, t * 2.5, c) }) },

  // ── ABSTRACT ─────────────────────────────────────────────────────────────────
  { id: 'abs-random-line', name: 'Random line grid', category: 'abstract', defaults: { spacing: 20 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 6}, (_, i) => {
        const x1 = rnd(i,13)*s; const y1 = rnd(i,14)*s;
        const len = s*0.15 + rnd(i,15)*s*0.25; const a = rnd(i,16)*Math.PI;
        return line(x1, y1, x1+len*Math.cos(a), y1+len*Math.sin(a), c, t);
      }).join('') }) },

  { id: 'abs-broken', name: 'Broken grid', category: 'abstract',
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M0,${f(s*0.3)} L${f(s*0.25)},${f(s*0.1)} L${f(s*0.6)},${f(s*0.25)} L${f(s)},${f(s*0.1)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<path d="M0,${f(s*0.7)} L${f(s*0.35)},${f(s*0.85)} L${f(s*0.7)},${f(s*0.6)} L${f(s)},${f(s*0.8)}" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'abs-fragment', name: 'Fragment grid', category: 'abstract', defaults: { spacing: 32 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<polygon points="${f(s*0.5)},${f(s*0.1)} ${f(s*0.9)},${f(s*0.4)} ${f(s*0.7)},${f(s*0.9)} ${f(s*0.2)},${f(s*0.8)} ${f(s*0.05)},${f(s*0.45)}" fill="none" stroke="${c}" stroke-width="${t}"/>` +
           `<polygon points="${f(s*0.5)},${f(s*0.3)} ${f(s*0.72)},${f(s*0.5)} ${f(s*0.55)},${f(s*0.75)} ${f(s*0.28)},${f(s*0.6)}" fill="${c}" opacity="0.12"/>` }) },

  { id: 'abs-mesh', name: 'Abstract mesh', category: 'abstract', defaults: { spacing: 28 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: [
        [0,s*0.3, s*0.5,0, s,s*0.4], [0,s*0.7, s*0.4,s, s,s*0.6],
        [s*0.2,0, s*0.5,s*0.5, s*0.8,s], [0,s*0.5, s,s*0.5],
      ].map(pts => `<polyline points="${pts.map(f).join(',')}" fill="none" stroke="${c}" stroke-width="${t}" opacity="0.7"/>`).join('') }) },

  { id: 'abs-fractal', name: 'Fractal grid', category: 'abstract', defaults: { spacing: 36 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: rect(s*0.1,s*0.1,s*0.8,s*0.8,c,false,t) + rect(s*0.25,s*0.25,s*0.5,s*0.5,c,false,t*0.7) + rect(s*0.38,s*0.38,s*0.24,s*0.24,c,false,t*0.45) +
           line(s*0.1,s*0.1,s*0.25,s*0.25,c,t*0.4) + line(s*0.9,s*0.1,s*0.75,s*0.25,c,t*0.4) +
           line(s*0.1,s*0.9,s*0.25,s*0.75,c,t*0.4) + line(s*0.9,s*0.9,s*0.75,s*0.75,c,t*0.4) }) },

  { id: 'abs-chaos', name: 'Chaos grid', category: 'abstract', defaults: { spacing: 24 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 10}, (_, i) => {
        const pts = Array.from({length: 3}, (_, j) => `${f(rnd(i,j+17)*s)},${f(rnd(i,j+20)*s)}`).join(' ');
        return `<polyline points="${pts}" fill="none" stroke="${c}" stroke-width="${f(t*(0.4+rnd(i,23)*0.6))}" opacity="${0.4+rnd(i,24)*0.6}"/>`;
      }).join('') }) },

  { id: 'abs-liquid', name: 'Liquid grid', category: 'abstract', defaults: { spacing: 40 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${f(s*0.5)},${f(s*0.15)} C${f(s*0.85)},${f(s*0.15)} ${f(s*0.9)},${f(s*0.85)} ${f(s*0.5)},${f(s*0.85)} C${f(s*0.1)},${f(s*0.85)} ${f(s*0.15)},${f(s*0.15)} ${f(s*0.5)},${f(s*0.15)} Z" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'abs-glitch', name: 'Digital glitch grid', category: 'abstract', defaults: { spacing: 20 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: Array.from({length: 6}, (_, i) => {
        const y = (s / 6) * i; const h = s / 6 - 1; const offset = (rnd(i, 25) - 0.5) * s * 0.3;
        return `<rect x="${f(offset)}" y="${f(y)}" width="${f(s)}" height="${f(h)}" fill="${c}" opacity="${0.08 + rnd(i, 26) * 0.12}"/>`;
      }).join('') + line(0, s/3, s, s/3, c, t) + line(s*0.1, s*0.65, s*0.9, s*0.65, c, t*0.6) }) },

  { id: 'abs-plasma', name: 'Plasma grid', category: 'abstract', defaults: { spacing: 32 },
    tile: ({ spacing: s, thickness: t, color: c }) => ({ tw: s, th: s,
      svg: `<path d="M${f(s*0.5)},${f(s*0.1)} Q${f(s*0.95)},${f(s*0.3)} ${f(s*0.8)},${f(s*0.7)} Q${f(s*0.6)},${f(s)},${f(s*0.2)},${f(s*0.85)} Q${f(-s*0.1)},${f(s*0.6)} ${f(s*0.15)},${f(s*0.3)} Q${f(s*0.3)},${f(-s*0.05)} ${f(s*0.5)},${f(s*0.1)} Z" fill="none" stroke="${c}" stroke-width="${t}"/>` }) },

  { id: 'abs-neural', name: 'AI neural grid', category: 'abstract', defaults: { spacing: 38 },
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const nodes = [[0.5,0.5],[0.15,0.2],[0.85,0.2],[0.15,0.8],[0.85,0.8]] as const;
      const edges = [[0,1],[0,2],[0,3],[0,4],[1,2],[3,4]] as const;
      return { tw: s, th: s,
        svg: edges.map(([a, b]) =>
          line(nodes[a][0]*s, nodes[a][1]*s, nodes[b][0]*s, nodes[b][1]*s, c, t*0.5)
        ).join('') + nodes.map(([x, y]) =>
          circle(x*s, y*s, t*2.5, c) + (x===0.5&&y===0.5 ? circle(x*s, y*s, s*0.1, c, false, t*0.5) : '')
        ).join('') };
    } },
];

// ─── Animated Pattern Definition ────────────────────────────────────────────────
export interface AnimatedPatternDef {
  id: string;
  name: string;
  category: 'animated';
  /** Static t=0 snapshot tile — used by Konva imageMap and export */
  tile: (cfg: PatternConfig) => { tw: number; th: number; svg: string };
  /** Full inline SVG string with embedded <style> CSS keyframe animations */
  getAnimatedSVG: (cfg: PatternConfig, width: number, height: number) => string;
}

export const ANIMATED_PATTERNS: AnimatedPatternDef[] = [
  // 1. anim-carbon-slash — diagonal slash lines + shimmer sweep
  {
    id: 'anim-carbon-slash',
    name: 'Carbon Slash',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<line x1="0" y1="${f(s)}" x2="${f(s)}" y2="0" stroke="${c}" stroke-width="${f(t)}" opacity="0.6"/>` +
           `<line x1="${f(-s*0.25)}" y1="${f(s)}" x2="${f(s*0.75)}" y2="0" stroke="${c}" stroke-width="${f(t)}" opacity="0.3"/>`,
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let tiles = '';
      const cols = Math.ceil(w / s) + 2;
      const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s; const y = row * s;
          tiles += `<line x1="${f(x)}" y1="${f(y+s)}" x2="${f(x+s)}" y2="${f(y)}" stroke="${c}" stroke-width="${f(t)}" opacity="0.6"/>`;
          tiles += `<line x1="${f(x-s*0.25)}" y1="${f(y+s)}" x2="${f(x+s*0.75)}" y2="${f(y)}" stroke="${c}" stroke-width="${f(t)}" opacity="0.3"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-shimmer-slash { from { transform: translateX(${f(-w)}px); } to { transform: translateX(${f(w*2)}px); } }
.vb-shimmer-slash { animation: vb-shimmer-slash 3s linear infinite; }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${tiles}</g>
<defs>
  <linearGradient id="vbcs-g" x1="0" y1="0" x2="1" y2="0">
    <stop offset="0%" stop-color="${c}" stop-opacity="0"/>
    <stop offset="40%" stop-color="${c}" stop-opacity="0.25"/>
    <stop offset="60%" stop-color="${c}" stop-opacity="0.25"/>
    <stop offset="100%" stop-color="${c}" stop-opacity="0"/>
  </linearGradient>
</defs>
<rect class="vb-shimmer-slash" x="0" y="0" width="${f(w*0.4)}" height="${h}" fill="url(#vbcs-g)" opacity="${f(op)}"/>
</svg>`;
    },
  },

  // 2. anim-labyrinth — nested rectangles with animated dash-offset crawl
  {
    id: 'anim-labyrinth',
    name: 'Labyrinth',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<rect x="${f(s*0.1)}" y="${f(s*0.1)}" width="${f(s*0.8)}" height="${f(s*0.8)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.4"/>` +
           `<rect x="${f(s*0.3)}" y="${f(s*0.3)}" width="${f(s*0.4)}" height="${f(s*0.4)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.25"/>`,
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let tiles = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      const perim1 = f(s * 0.8 * 4); const perim2 = f(s * 0.4 * 4);
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s; const y = row * s;
          tiles += `<rect x="${f(x+s*0.1)}" y="${f(y+s*0.1)}" width="${f(s*0.8)}" height="${f(s*0.8)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.4" stroke-dasharray="${perim1}" class="vb-lab-outer"/>`;
          tiles += `<rect x="${f(x+s*0.3)}" y="${f(y+s*0.3)}" width="${f(s*0.4)}" height="${f(s*0.4)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.25" stroke-dasharray="${perim2}" class="vb-lab-inner"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-lab-crawl { from { stroke-dashoffset: 0; } to { stroke-dashoffset: ${f(-s*3.2)}; } }
.vb-lab-outer { animation: vb-lab-crawl 4s linear infinite; }
.vb-lab-inner { animation: vb-lab-crawl 2.5s linear infinite reverse; }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${tiles}</g>
</svg>`;
    },
  },

  // 3. anim-hex-mosaic — hexagon grid with alternating pulse
  {
    id: 'anim-hex-mosaic',
    name: 'Hex Mosaic',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<polygon points="${hexPts(s/2, s/2, s*0.42)}" fill="none" stroke="${c}" stroke-width="${f(t)}"/>`,
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      const r = s * 0.42; const ri = s * 0.22;
      let tiles = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      let idx = 0;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const ox = col * s + (row % 2 === 0 ? 0 : s * 0.5);
          const oy = row * s * 0.87;
          const delay = f(((idx % 3) * 1.0));
          tiles += `<polygon points="${hexPts(ox+s/2, oy+s/2, r)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.4"/>`;
          tiles += `<polygon points="${hexPts(ox+s/2, oy+s/2, ri)}" fill="${c}" opacity="0.1" style="animation:vb-hex-pulse 3s ease-in-out ${delay}s infinite"/>`;
          idx++;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-hex-pulse { 0%,100% { opacity:0.1; } 50% { opacity:0.45; } }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${tiles}</g>
</svg>`;
    },
  },

  // 4. anim-angular-cuts — polygon slash shapes with parallax translateX
  {
    id: 'anim-angular-cuts',
    name: 'Angular Cuts',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<polygon points="0,${f(s*0.2)} ${f(s*0.6)},0 ${f(s)},${f(s*0.8)} ${f(s*0.4)},${f(s)}" fill="${c}" opacity="0.15"/>` +
           `<line x1="0" y1="${f(s*0.5)}" x2="${f(s)}" y2="${f(s*0.5)}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.3"/>`,
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let slowLayer = ''; let fastLayer = '';
      const cols = Math.ceil(w / s) + 4; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -2; col < cols; col++) {
          const x = col * s; const y = row * s;
          slowLayer += `<polygon points="${f(x)},${f(y+s*0.2)} ${f(x+s*0.6)},${f(y)} ${f(x+s)},${f(y+s*0.8)} ${f(x+s*0.4)},${f(y+s)}" fill="${c}" opacity="0.15"/>`;
          fastLayer += `<line x1="${f(x)}" y1="${f(y+s*0.5)}" x2="${f(x+s)}" y2="${f(y+s*0.5)}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.3"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-ac-move { from { transform: translateX(0px); } to { transform: translateX(${f(s)}px); } }
.vb-ac-slow { animation: vb-ac-move 8s linear infinite; }
.vb-ac-fast { animation: vb-ac-move 4s linear infinite; }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">
  <g class="vb-ac-slow">${slowLayer}</g>
  <g class="vb-ac-fast">${fastLayer}</g>
</g>
</svg>`;
    },
  },

  // 5. anim-contour-flow — topographic wave lines drifting downward
  {
    id: 'anim-contour-flow',
    name: 'Contour Flow',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s * 4, th: s,
      svg: [0.25, 0.5, 0.75].map(fy =>
        `<path d="${wave(0, s*fy, s*4, s*0.12, 2)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>`
      ).join(''),
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let rows = '';
      const rowCount = Math.ceil(h / s) + 3;
      for (let row = -2; row < rowCount; row++) {
        const y = row * s;
        rows += `<path d="${wave(0, y + s*0.5, w + s, s*0.1, 3)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>`;
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-contour-drift { from { transform: translateY(0px); } to { transform: translateY(${f(s)}px); } }
.vb-contour { animation: vb-contour-drift 6s linear infinite; }
</style>
<g class="vb-contour" opacity="${f(op)}" transform-origin="${f(w/2)} ${f(h/2)}"
   style="transform-box:fill-box;" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${rows}</g>
</svg>`;
    },
  },

  // 6. anim-low-poly — triangle mesh with staggered opacity flicker
  {
    id: 'anim-low-poly',
    name: 'Low Poly',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<polygon points="0,0 ${f(s)},0 ${f(s*0.5)},${f(s)}" fill="${c}" opacity="0.15" stroke="${c}" stroke-width="${f(t*0.5)}"/>` +
           `<polygon points="${f(s)},0 ${f(s)},${f(s)} ${f(s*0.5)},${f(s)}" fill="${c}" opacity="0.08" stroke="${c}" stroke-width="${f(t*0.5)}"/>`,
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let tris = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s; const y = row * s;
          const delay = f(((row + col) % 5) * 0.4);
          tris += `<polygon points="${f(x)},${f(y)} ${f(x+s)},${f(y)} ${f(x+s*0.5)},${f(y+s)}" fill="${c}" stroke="${c}" stroke-width="${f(t*0.5)}" style="opacity:0.2;animation:vb-lp-flicker 3s ease-in-out ${delay}s infinite"/>`;
          tris += `<polygon points="${f(x+s)},${f(y)} ${f(x+s)},${f(y+s)} ${f(x+s*0.5)},${f(y+s)}" fill="${c}" stroke="${c}" stroke-width="${f(t*0.5)}" style="opacity:0.1;animation:vb-lp-flicker 3s ease-in-out ${f(Number(delay)+0.2)}s infinite"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-lp-flicker { 0%,100% { opacity:0.2; } 50% { opacity:0.65; } }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${tris}</g>
</svg>`;
    },
  },

  // 7. anim-herringbone — chevron weave with shimmer scan
  {
    id: 'anim-herringbone',
    name: 'Herringbone',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s * 2, th: s,
      svg: `<polyline points="0,${f(s*0.5)} ${f(s*0.5)},0 ${f(s)},${f(s*0.5)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.7"/>` +
           `<polyline points="${f(s)},${f(s*0.5)} ${f(s*1.5)},${f(s)} ${f(s*2)},${f(s*0.5)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.7"/>`,
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let chevrons = '';
      const cols = Math.ceil(w / (s*2)) + 2; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s * 2; const y = row * s;
          chevrons += `<polyline points="${f(x)},${f(y+s*0.5)} ${f(x+s*0.5)},${f(y)} ${f(x+s)},${f(y+s*0.5)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.7"/>`;
          chevrons += `<polyline points="${f(x+s)},${f(y+s*0.5)} ${f(x+s*1.5)},${f(y+s)} ${f(x+s*2)},${f(y+s*0.5)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.7"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-hb-shimmer { from { transform: translateX(${f(-w)}px); } to { transform: translateX(${f(w)}px); } }
.vb-hb-shimmer { animation: vb-hb-shimmer 5s linear infinite; }
</style>
<defs>
  <linearGradient id="vb-hb-g" x1="0" y1="0" x2="1" y2="0">
    <stop offset="0%" stop-color="${c}" stop-opacity="0"/>
    <stop offset="45%" stop-color="${c}" stop-opacity="0.2"/>
    <stop offset="55%" stop-color="${c}" stop-opacity="0.2"/>
    <stop offset="100%" stop-color="${c}" stop-opacity="0"/>
  </linearGradient>
</defs>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${chevrons}</g>
<rect class="vb-hb-shimmer" x="${f(-w*0.2)}" y="0" width="${f(w*0.4)}" height="${h}" fill="url(#vb-hb-g)" opacity="${f(op)}"/>
</svg>`;
    },
  },

  // 8. anim-isometric-cube — isometric cubes with face light cycling
  {
    id: 'anim-isometric-cube',
    name: 'Isometric Cube',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const hw = s * 0.5; const hh = s * 0.29;
      return {
        tw: s, th: s,
        svg: `<polygon points="${f(hw)},0 ${f(s)},${f(hh)} ${f(hw)},${f(hh*2)} 0,${f(hh)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>` +
             `<polygon points="${f(hw)},${f(hh*2)} ${f(s)},${f(hh)} ${f(s)},${f(hh*3)} ${f(hw)},${f(hh*4)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.35"/>` +
             `<polygon points="${f(hw)},${f(hh*2)} 0,${f(hh)} 0,${f(hh*3)} ${f(hw)},${f(hh*4)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.25"/>`,
      };
    },
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      const hw = s * 0.5; const hh = s * 0.29;
      let cubes = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s + (row % 2 === 0 ? 0 : s * 0.5); const y = row * s * 0.58;
          const cx = x + hw; const cy = y;
          cubes += `<polygon points="${f(cx)},${f(cy)} ${f(cx+hw)},${f(cy+hh)} ${f(cx)},${f(cy+hh*2)} ${f(cx-hw)},${f(cy+hh)}" fill="${c}" stroke="${c}" stroke-width="${f(t*0.5)}" style="opacity:0.2;animation:vb-cube-top 4s ease-in-out 0s infinite"/>`;
          cubes += `<polygon points="${f(cx)},${f(cy+hh*2)} ${f(cx+hw)},${f(cy+hh)} ${f(cx+hw)},${f(cy+hh*3)} ${f(cx)},${f(cy+hh*4)}" fill="${c}" stroke="${c}" stroke-width="${f(t*0.5)}" style="opacity:0.12;animation:vb-cube-top 4s ease-in-out -1.3s infinite"/>`;
          cubes += `<polygon points="${f(cx)},${f(cy+hh*2)} ${f(cx-hw)},${f(cy+hh)} ${f(cx-hw)},${f(cy+hh*3)} ${f(cx)},${f(cy+hh*4)}" fill="${c}" stroke="${c}" stroke-width="${f(t*0.5)}" style="opacity:0.08;animation:vb-cube-top 4s ease-in-out -2.6s infinite"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-cube-top { 0%,100% { opacity:0.1; } 50% { opacity:0.4; } }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${cubes}</g>
</svg>`;
    },
  },

  // 9. anim-knot-weave — diagonal cross lines with horizontal layer sliding
  {
    id: 'anim-knot-weave',
    name: 'Knot Weave',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<line x1="0" y1="0" x2="${f(s)}" y2="${f(s)}" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>` +
           `<line x1="${f(s)}" y1="0" x2="0" y2="${f(s)}" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>` +
           circle(s/2, s/2, t * 1.5, c),
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let baseGrid = ''; let diagLayer = '';
      const cols = Math.ceil(w / s) + 4; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -2; col < cols; col++) {
          const x = col * s; const y = row * s;
          baseGrid += `<line x1="${f(x)}" y1="${f(y)}" x2="${f(x+s)}" y2="${f(y+s)}" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>`;
          baseGrid += circle(x+s/2, y+s/2, t*1.5, c);
          diagLayer += `<line x1="${f(x+s)}" y1="${f(y)}" x2="${f(x)}" y2="${f(y+s)}" stroke="${c}" stroke-width="${f(t)}" opacity="0.35"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-knot-slide { from { transform: translateX(0px); } to { transform: translateX(${f(s)}px); } }
.vb-knot-slide { animation: vb-knot-slide 5s linear infinite; }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">
  <g>${baseGrid}</g>
  <g class="vb-knot-slide">${diagLayer}</g>
</g>
</svg>`;
    },
  },

  // 10. anim-diamond-quilt — diamond outlines + dots + slow rotation overlay
  {
    id: 'anim-diamond-quilt',
    name: 'Diamond Quilt',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<polygon points="${f(s*0.5)},0 ${f(s)},${f(s*0.5)} ${f(s*0.5)},${f(s)} 0,${f(s*0.5)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>` +
           circle(s/2, s/2, t * 1.2, c),
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let diamonds = ''; let dots = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s; const y = row * s;
          diamonds += `<polygon points="${f(x+s*0.5)},${f(y)} ${f(x+s)},${f(y+s*0.5)} ${f(x+s*0.5)},${f(y+s)} ${f(x)},${f(y+s*0.5)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>`;
          dots += circle(x+s/2, y+s/2, t*1.2, c);
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-dq-rot { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes vb-dq-dot { 0%,100% { opacity:0.4; } 50% { opacity:0.9; } }
.vb-dq-overlay { transform-origin: ${f(w/2)}px ${f(h/2)}px; animation: vb-dq-rot 12s linear infinite; }
.vb-dq-dots { animation: vb-dq-dot 3s ease-in-out infinite; }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">
  <g>${diamonds}</g>
  <g class="vb-dq-dots">${dots}</g>
  <rect class="vb-dq-overlay" x="${f(-w)}" y="${f(-h)}" width="${f(w*3)}" height="${f(h*3)}" fill="none" stroke="${c}" stroke-width="${f(t*0.3)}" opacity="0.1" stroke-dasharray="${f(s*0.5)} ${f(s*0.25)}"/>
</g>
</svg>`;
    },
  },

  // 11. anim-halftone — dots pulsing in wave from top-left
  {
    id: 'anim-halftone',
    name: 'Halftone Wave',
    category: 'animated',
    tile: ({ spacing: s, color: c }) => ({
      tw: s, th: s,
      svg: circle(s/2, s/2, s * 0.22, c),
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { color: c, opacity: op, thickness: t, rotation: rot } = cfg;
      let dots = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      const rPeak = s * 0.28;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s + s/2; const y = row * s + s/2;
          const dist = Math.sqrt(col*col + row*row);
          const delay = f(dist * 0.15);
          dots += `<circle cx="${f(x)}" cy="${f(y)}" r="${f(rPeak)}" fill="${c}" stroke="none" style="transform-box:fill-box;transform-origin:center;animation:vb-ht-pulse ${f(t > 2 ? 2 : 3)}s ease-in-out ${delay}s infinite"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-ht-pulse { 0%,100% { transform: scale(0.3); opacity: 0.25; } 50% { transform: scale(1); opacity: 0.65; } }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${dots}</g>
</svg>`;
    },
  },

  // 12. anim-voronoi — irregular polygon cells with staggered opacity pulse
  {
    id: 'anim-voronoi',
    name: 'Voronoi Pulse',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const pts = Array.from({length: 6}, (_, i) => {
        const a = (Math.PI * 2 / 6) * i;
        const r = s * (0.35 + rnd(i, 7) * 0.15);
        return `${f(s/2 + r*Math.cos(a))},${f(s/2 + r*Math.sin(a))}`;
      }).join(' ');
      return { tw: s, th: s, svg: `<polygon points="${pts}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.4"/>` };
    },
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let cells = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const cx = col * s + s/2; const cy = row * s + s/2;
          const pts = Array.from({length: 6}, (_, i) => {
            const a = (Math.PI * 2 / 6) * i;
            const r = s * (0.35 + rnd(col+i, row+i, 3) * 0.15);
            return `${f(cx + r*Math.cos(a))},${f(cy + r*Math.sin(a))}`;
          }).join(' ');
          const delay = f(rnd(col, row) * 2.5);
          cells += `<polygon points="${pts}" fill="${c}" stroke="${c}" stroke-width="${f(t*0.5)}" style="opacity:0.15;animation:vb-vor-pulse 3.5s ease-in-out ${delay}s infinite"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-vor-pulse { 0%,100% { opacity:0.15; } 50% { opacity:0.6; } }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${cells}</g>
</svg>`;
    },
  },

  // 13. anim-star-tessellation — 6-pointed star pattern slowly rotating
  {
    id: 'anim-star-tessellation',
    name: 'Star Tessellation',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<polygon points="${starPts(s/2, s/2, s*0.42, s*0.18, 6)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.6"/>`,
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let stars = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const cx = col * s + s/2; const cy = row * s + s/2;
          const delay = f(((row + col) % 4) * 2.0);
          stars += `<polygon points="${starPts(cx, cy, s*0.42, s*0.18, 6)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.6" style="transform-origin:${f(cx)}px ${f(cy)}px;animation:vb-star-rot 8s linear ${delay}s infinite"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-star-rot { from { transform: rotate(0deg); } to { transform: rotate(60deg); } }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${stars}</g>
</svg>`;
    },
  },

  // 14. anim-neon-grid — thin grid + corner dots + vertical scan line
  {
    id: 'anim-neon-grid',
    name: 'Neon Grid',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<line x1="${f(s/2)}" y1="0" x2="${f(s/2)}" y2="${f(s)}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.3"/>` +
           `<line x1="0" y1="${f(s/2)}" x2="${f(s)}" y2="${f(s/2)}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.3"/>` +
           circle(s/2, s/2, t * 1.5, c),
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let gridLines = ''; let dots = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s; const y = row * s;
          gridLines += `<line x1="${f(x+s/2)}" y1="${f(y)}" x2="${f(x+s/2)}" y2="${f(y+s)}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.3"/>`;
          gridLines += `<line x1="${f(x)}" y1="${f(y+s/2)}" x2="${f(x+s)}" y2="${f(y+s/2)}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.3"/>`;
          dots += `<circle cx="${f(x+s/2)}" cy="${f(y+s/2)}" r="${f(t*1.5)}" fill="${c}" style="animation:vb-ng-dot 2s ease-in-out ${f(rnd(col,row)*1.5)}s infinite"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-ng-scan { from { transform: translateY(${f(-h)}px); } to { transform: translateY(${f(h)}px); } }
@keyframes vb-ng-dot { 0%,100% { opacity:0.4; } 50% { opacity:1; } }
.vb-ng-scan { animation: vb-ng-scan 4s linear infinite; }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">
  <g>${gridLines}</g>
  <g>${dots}</g>
  <rect class="vb-ng-scan" x="0" y="0" width="${w}" height="${f(t*3)}" fill="${c}" opacity="0.15"/>
</g>
</svg>`;
    },
  },

  // 15. anim-brushed-metal — horizontal micro-lines + gloss sweep
  {
    id: 'anim-brushed-metal',
    name: 'Brushed Metal',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: Array.from({length: Math.floor(s / 3)}, (_, i) => {
        const y = i * 3 + 1.5; const op = 0.1 + rnd(i, 99) * 0.2;
        return `<line x1="0" y1="${f(y)}" x2="${f(s)}" y2="${f(y)}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="${f(op)}"/>`;
      }).join(''),
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let lines = '';
      const lineCount = Math.ceil(h / 3) + 2;
      for (let i = 0; i < lineCount; i++) {
        const y = i * 3 + 1.5; const lineOp = 0.1 + rnd(i, 99) * 0.2;
        lines += `<line x1="0" y1="${f(y)}" x2="${f(w)}" y2="${f(y)}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="${f(lineOp)}"/>`;
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-bm-gloss { 0% { transform: translateX(${f(-w*0.3)}px); } 100% { transform: translateX(${f(w*1.3)}px); } }
.vb-bm-gloss { animation: vb-bm-gloss 4s ease-in-out infinite; }
</style>
<defs>
  <linearGradient id="vb-bm-g" x1="0" y1="0" x2="1" y2="0">
    <stop offset="0%" stop-color="white" stop-opacity="0"/>
    <stop offset="45%" stop-color="white" stop-opacity="0.18"/>
    <stop offset="55%" stop-color="white" stop-opacity="0.18"/>
    <stop offset="100%" stop-color="white" stop-opacity="0"/>
  </linearGradient>
</defs>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">${lines}</g>
<rect class="vb-bm-gloss" x="0" y="0" width="${f(w*0.3)}" height="${h}" fill="url(#vb-bm-g)" opacity="${f(op)}"/>
</svg>`;
    },
  },

  // 16. anim-mandala — concentric rings CW + CCW rotation
  {
    id: 'anim-mandala',
    name: 'Mandala',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: circle(s/2, s/2, s*0.4, c, false, t) +
           circle(s/2, s/2, s*0.22, c, false, t * 0.7) +
           Array.from({length: 8}, (_, i) => {
             const a = (Math.PI / 4) * i;
             return `<line x1="${f(s/2)}" y1="${f(s/2)}" x2="${f(s/2 + s*0.4*Math.cos(a))}" y2="${f(s/2 + s*0.4*Math.sin(a))}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.4"/>`;
           }).join(''),
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const { spacing: _s, thickness: t, color: c, opacity: op } = cfg;
      const cx = w / 2; const cy = h / 2;
      const r1 = Math.min(w, h) * 0.38; const r2 = Math.min(w, h) * 0.22; const r3 = Math.min(w, h) * 0.12;
      const outerSpokes = Array.from({length: 12}, (_, i) => {
        const a = (Math.PI / 6) * i;
        return `<line x1="${f(cx)}" y1="${f(cy)}" x2="${f(cx + r1*Math.cos(a))}" y2="${f(cy + r1*Math.sin(a))}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.35"/>`;
      }).join('');
      const innerSpokes = Array.from({length: 8}, (_, i) => {
        const a = (Math.PI / 4) * i;
        return `<line x1="${f(cx)}" y1="${f(cy)}" x2="${f(cx + r2*Math.cos(a))}" y2="${f(cy + r2*Math.sin(a))}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.5"/>`;
      }).join('');
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-mnd-cw { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes vb-mnd-ccw { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
.vb-mnd-outer { transform-origin: ${f(cx)}px ${f(cy)}px; animation: vb-mnd-cw 20s linear infinite; }
.vb-mnd-inner { transform-origin: ${f(cx)}px ${f(cy)}px; animation: vb-mnd-ccw 12s linear infinite; }
</style>
<g opacity="${f(op)}">
  <circle cx="${f(cx)}" cy="${f(cy)}" r="${f(t*2.5)}" fill="${c}"/>
  <g class="vb-mnd-outer">
    <circle cx="${f(cx)}" cy="${f(cy)}" r="${f(r1)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>
    ${outerSpokes}
  </g>
  <g class="vb-mnd-inner">
    <circle cx="${f(cx)}" cy="${f(cy)}" r="${f(r2)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.6"/>
    ${innerSpokes}
    <circle cx="${f(cx)}" cy="${f(cy)}" r="${f(r3)}" fill="none" stroke="${c}" stroke-width="${f(t*0.7)}" opacity="0.7"/>
  </g>
</g>
</svg>`;
    },
  },

  // 17. anim-particle-web — scattered nodes + drifting edges
  {
    id: 'anim-particle-web',
    name: 'Particle Web',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const nodes = Array.from({length: 4}, (_, i) => [rnd(i,30)*s, rnd(i,31)*s] as [number, number]);
      const edges = [[0,1],[1,2],[2,3],[3,0]] as const;
      return {
        tw: s, th: s,
        svg: edges.map(([a,b]) => `<line x1="${f(nodes[a][0])}" y1="${f(nodes[a][1])}" x2="${f(nodes[b][0])}" y2="${f(nodes[b][1])}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.3"/>`).join('') +
             nodes.map(([x,y]) => circle(x, y, t * 1.5, c)).join(''),
      };
    },
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      const nodeCount = Math.min(40, Math.ceil((w * h) / (s * s * 4)));
      type Node = { x: number; y: number; dx: number; dy: number };
      const nodes: Node[] = Array.from({length: nodeCount}, (_, i) => ({
        x: rnd(i, 40) * w,
        y: rnd(i, 41) * h,
        dx: (rnd(i, 42) - 0.5) * s * 0.6,
        dy: (rnd(i, 43) - 0.5) * s * 0.6,
      }));
      const threshold = s * 2.5;
      let edges = '';
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x; const dy = nodes[i].y - nodes[j].y;
          if (Math.sqrt(dx*dx + dy*dy) < threshold) {
            edges += `<line x1="${f(nodes[i].x)}" y1="${f(nodes[i].y)}" x2="${f(nodes[j].x)}" y2="${f(nodes[j].y)}" stroke="${c}" stroke-width="${f(t*0.5)}" opacity="0.25"/>`;
          }
        }
      }
      const dots = nodes.map((n, i) => {
        const dur = f(3 + rnd(i, 44) * 4);
        const delay = f(rnd(i, 45) * -3);
        return `<circle cx="${f(n.x)}" cy="${f(n.y)}" r="${f(t*1.8)}" fill="${c}" style="--dx:${f(n.dx)}px;--dy:${f(n.dy)}px;animation:vb-pw-drift ${dur}s ease-in-out ${delay}s infinite alternate"/>`;
      }).join('');
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-pw-drift { from { transform: translate(0,0); } to { transform: translate(var(--dx),var(--dy)); } }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">
  ${edges}
  ${dots}
</g>
</svg>`;
    },
  },

  // 18. anim-plasma-sine — sine wave rows with phase offset
  {
    id: 'anim-plasma-sine',
    name: 'Plasma Sine',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s * 4, th: s,
      svg: [0, 0.33, 0.66].map(fy =>
        `<path d="${wave(0, s*(0.2+fy*0.6), s*4, s*0.08, 3)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="${f(0.3+fy*0.3)}"/>`
      ).join(''),
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let wave1 = ''; let wave2 = '';
      const rowCount = Math.ceil(h / (s * 0.8)) + 3;
      for (let i = 0; i < rowCount; i++) {
        const y = i * s * 0.8 - s;
        wave1 += `<path d="${wave(-s, y + s*0.4, w + s*2, s*0.1, 4)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="${f(0.25+((i%3)*0.1))}"/>`;
        wave2 += `<path d="${wave(-s, y + s*0.7, w + s*2, s*0.07, 5)}" fill="none" stroke="${c}" stroke-width="${f(t*0.7)}" opacity="${f(0.15+((i%3)*0.08))}"/>`;
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-ps-shift { from { transform: translateX(0px); } to { transform: translateX(${f(-w)}px); } }
.vb-ps-w1 { animation: vb-ps-shift 4s linear infinite; }
.vb-ps-w2 { animation: vb-ps-shift 6s linear infinite; }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">
  <g class="vb-ps-w1">${wave1}</g>
  <g class="vb-ps-w2">${wave2}</g>
</g>
</svg>`;
    },
  },

  // 19. anim-circuit-traces — PCB traces with animated signal dot
  {
    id: 'anim-circuit-traces',
    name: 'Circuit Traces',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => ({
      tw: s, th: s,
      svg: `<path d="M${f(s*0.1)},${f(s*0.5)} H${f(s*0.5)} V${f(s*0.1)}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.6"/>` +
           circle(s*0.1, s*0.5, t * 2, c) +
           circle(s*0.5, s*0.1, t * 2, c),
    }),
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let traces = ''; let pads = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      const pathLen = s * 0.8;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s; const y = row * s;
          const pathData = `M${f(x+s*0.1)},${f(y+s*0.5)} H${f(x+s*0.5)} V${f(y+s*0.1)}`;
          traces += `<path d="${pathData}" fill="none" stroke="${c}" stroke-width="${f(t)}" opacity="0.5"/>`;
          traces += `<path d="${pathData}" fill="none" stroke="${c}" stroke-width="${f(t*2)}" opacity="0.7" stroke-dasharray="${f(t*3)} ${f(pathLen)}" class="vb-ct-signal"/>`;
          pads += circle(x+s*0.1, y+s*0.5, t*2, c) + circle(x+s*0.5, y+s*0.1, t*2, c);
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-ct-run { from { stroke-dashoffset: 0; } to { stroke-dashoffset: ${f(-pathLen * 2)}; } }
.vb-ct-signal { animation: vb-ct-run 3s linear infinite; }
.vb-ct-signal:nth-child(odd) { animation-delay: -1.5s; }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">
  ${traces}
  ${pads}
</g>
</svg>`;
    },
  },

  // 20. anim-spliced-glass — shattered triangular glass faces glinting
  {
    id: 'anim-spliced-glass',
    name: 'Spliced Glass',
    category: 'animated',
    tile: ({ spacing: s, thickness: t, color: c }) => {
      const pts1 = `0,0 ${f(s)},${f(s*0.35)} ${f(s*0.4)},${f(s)}`;
      const pts2 = `${f(s)},0 ${f(s)},${f(s*0.35)} ${f(s*0.4)},${f(s)}`;
      return {
        tw: s, th: s,
        svg: `<polygon points="${pts1}" fill="${c}" opacity="0.06" stroke="${c}" stroke-width="${f(t*0.5)}"/>` +
             `<polygon points="${pts2}" fill="${c}" opacity="0.04" stroke="${c}" stroke-width="${f(t*0.5)}"/>`,
      };
    },
    getAnimatedSVG: (cfg, w, h) => {
      const s = Math.max(1, cfg.spacing);
      const { thickness: t, color: c, opacity: op, rotation: rot } = cfg;
      let shards = ''; let edges = '';
      const cols = Math.ceil(w / s) + 2; const rows = Math.ceil(h / s) + 2;
      for (let row = -1; row < rows; row++) {
        for (let col = -1; col < cols; col++) {
          const x = col * s; const y = row * s;
          const ox = f(x + rnd(col, row, 1) * s * 0.3);
          const oy = f(y + rnd(col, row, 2) * s * 0.3);
          const ox2 = f(x + s * (0.4 + rnd(col, row, 3) * 0.3));
          const oy2 = f(y + s * (0.5 + rnd(col, row, 4) * 0.3));
          const pts = `${f(x)},${f(y)} ${f(x+s)},${f(y)} ${f(x+s)},${f(y+s)} ${f(x)},${f(y+s)} ${ox},${oy2}`;
          const delay = f(rnd(col, row, 5) * 3);
          shards += `<polygon points="${pts}" fill="${c}" stroke="none" style="opacity:0.04;animation:vb-sg-glint 4s ease-in-out ${delay}s infinite"/>`;
          edges += `<line x1="${f(x)}" y1="${f(y)}" x2="${ox2}" y2="${oy2}" stroke="${c}" stroke-width="${f(t*0.4)}" opacity="0.2"/>`;
          edges += `<line x1="${f(x+s)}" y1="${f(y+s)}" x2="${ox2}" y2="${oy2}" stroke="${c}" stroke-width="${f(t*0.4)}" opacity="0.2"/>`;
        }
      }
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<style>
@keyframes vb-sg-glint { 0%,100% { opacity:0.04; } 50% { opacity:0.28; } }
</style>
<g opacity="${f(op)}" transform="rotate(${f(rot)},${f(w/2)},${f(h/2)})">
  ${shards}
  ${edges}
</g>
</svg>`;
    },
  },
];

const ANIM_PATTERN_BY_ID = new Map<string, AnimatedPatternDef>(
  ANIMATED_PATTERNS.map(p => [p.id, p])
);

export function getAnimatedPatternById(id: string): AnimatedPatternDef | undefined {
  return ANIM_PATTERN_BY_ID.get(id);
}

export function getAnimatedPatternsForSidebar(): AnimatedPatternDef[] {
  return ANIMATED_PATTERNS;
}

// ─── Lookups ────────────────────────────────────────────────────────────────────
const PATTERN_BY_ID = new Map<string, PatternDef>(PATTERNS.map(p => [p.id, p]));

export function getPatternsByCategory(categoryId: string): PatternDef[] {
  return PATTERNS.filter(p => p.category === categoryId);
}

export function getPatternById(id: string): PatternDef | undefined {
  return PATTERN_BY_ID.get(id);
}

// ─── SVG Generation ────────────────────────────────────────────────────────────
export function generatePatternSVG(
  patternId: string,
  config: PatternConfig,
  width: number,
  height: number
): string {
  const def = PATTERN_BY_ID.get(patternId);
  if (!def) return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect width="${width}" height="${height}" fill="transparent"/></svg>`;

  const cfg = { ...DEFAULT_PATTERN_CONFIG, ...config };
  const { tw, th, svg } = def.tile(cfg);

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
  <defs>
    <pattern id="vbpat" x="0" y="0" width="${f(tw)}" height="${f(th)}" patternUnits="userSpaceOnUse" patternTransform="rotate(${cfg.rotation})">
      ${svg}
    </pattern>
  </defs>
  <rect width="${width}" height="${height}" fill="url(#vbpat)" opacity="${cfg.opacity}"/>
</svg>`;
}

/** Returns a tiny 56×56 preview SVG for the sidebar thumbnail */
export function generatePatternPreview(patternId: string, color = '#ffffff', opacity = 0.8): string {
  const def = PATTERN_BY_ID.get(patternId);
  if (!def) return '';
  const previewSpacing = Math.max(8, Math.min(20, (def.defaults?.spacing ?? 20) * 0.55));
  const cfg: PatternConfig = {
    ...DEFAULT_PATTERN_CONFIG, color, opacity,
    spacing: previewSpacing,
    thickness: def.defaults?.thickness ?? 1,
    rotation: 0, blendMode: 'normal',
  };
  const { tw, th, svg } = def.tile(cfg);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" style="border-radius:6px">
  <rect width="56" height="56" fill="#18181b" rx="6"/>
  <defs>
    <pattern id="prev" x="0" y="0" width="${f(tw)}" height="${f(th)}" patternUnits="userSpaceOnUse">
      ${svg}
    </pattern>
  </defs>
  <rect width="56" height="56" fill="url(#prev)" rx="6"/>
</svg>`;
}

/** Regenerate SVG for a pattern element with updated config */
export function reapplyPatternConfig(
  patternId: string,
  config: PatternConfig,
  width: number,
  height: number
): string {
  return generatePatternSVG(patternId, config, width, height);
}
